import { useList } from "@refinedev/core";
import { Alert, Col, Row } from "antd";
import {
  DocumentSummaryStats,
  DocumentPositionPoint,
  DocumentReaderSegment,
} from "types/";
import { useAnalyticsPage } from "./hooks/useAnalyticsPage";
import { AnalyticsSubPageLayout } from "../components/analytics/AnalyticsSubPageLayout";
import { CompactStatCard } from "../components/analytics/CompactStatCard";
import {
  TARGETS,
  ANALYTICS_STALE_TIME,
} from "../components/analytics/constants/constants";
import { buildDateFilters } from "../components/analytics/utils/";
import { ReadingFunnelCard } from "../components/analytics/ReadingFunnelCard";
import { DocumentReaderSegmentsChart } from "../components/analytics/DocumentReaderSegmentsChart";
import { ReadingDepthCurveChart } from "../components/analytics/ReadingDepthCurveChart";
import { ScrollDensityChart } from "../components/analytics/ScrollDensityChart";

const T_PREFIX = "media.projects.analytics.documentHighlighter";

export function DocumentHighlighterAnalyticsPage() {
  const page = useAnalyticsPage<DocumentSummaryStats>({
    analyticsType: "document",
  });
  const {
    summaryData: documentData,
    isDataLoading,
    t,
    projectId,
    dateRange,
  } = page;

  const dateFilters = buildDateFilters(dateRange);
  const dateEnabled =
    !!projectId && !!dateRange.startDate && !!dateRange.endDate;

  const {
    data: positionAnalysis,
    isLoading: isPositionLoading,
    isFetching: isPositionFetching,
  } = useList<DocumentPositionPoint>({
    resource: `analytics/projects/${projectId}/analytics/document/position-analysis`,
    config: { filters: dateFilters },
    queryOptions: {
      enabled: !!projectId && dateEnabled,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const {
    data: readerSegments,
    isLoading: isSegmentsLoading,
    isFetching: isSegmentsFetching,
  } = useList<DocumentReaderSegment>({
    resource: `analytics/projects/${projectId}/analytics/document/reader-segments`,
    config: { filters: dateFilters },
    queryOptions: { enabled: dateEnabled, staleTime: ANALYTICS_STALE_TIME },
    errorNotification: false,
  });

  const positionData = positionAnalysis?.data;
  const isPositionDataLoading = isPositionLoading || isPositionFetching;
  const segmentsData = readerSegments?.data;
  const isSegmentsDataLoading = isSegmentsLoading || isSegmentsFetching;

  return (
    <AnalyticsSubPageLayout
      authKey="documentHighlighterAnalytics"
      translationPrefix={T_PREFIX}
      page={page}
    >
      <Row gutter={[8, 8]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.readRate`)}
            metric={documentData?.avg_reading_depth_pct}
            target={TARGETS.documentHighlighter.avg_reading_depth_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.readRateTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.totalSessions`)}
            metric={documentData?.total_sessions}
            loading={isDataLoading}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.fullCompleters`)}
            metric={documentData?.full_completers_pct}
            target={TARGETS.documentHighlighter.full_completers_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
          />
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ width: "100%" }}>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.scrollRate`)}
            metric={documentData?.scroll_rate_pct}
            target={TARGETS.documentHighlighter.scroll_rate_pct}
            aim="high"
            type="percentage"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.scrollRateTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.backtrackRate`)}
            metric={documentData?.backtrack_rate_pct}
            target={45}
            type="percentage"
            aim="low"
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.backtrackRateTooltip`)}
          />
        </Col>
        <Col sm={24} md={8}>
          <CompactStatCard
            label={t(`${T_PREFIX}.biggestDrop`)}
            metric={
              documentData?.biggest_drop_pp == null
                ? null
                : Math.round(documentData.biggest_drop_pp)
            }
            type="percentage"
            suffix={
              documentData?.biggest_drop_page_number == null
                ? undefined
                : t(`${T_PREFIX}.biggestDropSuffix`, {
                    // page_number is 0-indexed from the API; show it 1-based.
                    page: documentData.biggest_drop_page_number + 1,
                  })
            }
            loading={isDataLoading}
            tooltip={t(`${T_PREFIX}.biggestDropTooltip`)}
          />
        </Col>
      </Row>

      {documentData?.biggest_drop_pp != null &&
        documentData?.biggest_drop_page_number != null && (
          <Alert
            message={t(`${T_PREFIX}.biggestDropAlert`, {
              drop: Math.abs(Math.round(documentData.biggest_drop_pp)),
              // page_number is 0-indexed from the API; show it 1-based.
              page: documentData.biggest_drop_page_number + 1,
            })}
            type="warning"
            showIcon
          />
        )}

      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col sm={24} md={12}>
          <ReadingFunnelCard data={documentData} loading={isDataLoading} />
        </Col>
        <Col sm={24} md={12}>
          <DocumentReaderSegmentsChart
            data={segmentsData}
            loading={isSegmentsDataLoading}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col sm={24} md={12}>
          <ReadingDepthCurveChart
            data={positionData}
            loading={isPositionDataLoading}
          />
        </Col>
        <Col sm={24} md={12}>
          <ScrollDensityChart
            data={positionData}
            loading={isPositionDataLoading}
          />
        </Col>
      </Row>
    </AnalyticsSubPageLayout>
  );
}
